import { memo, useEffect, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { NodeResizer } from '@xyflow/react'

/**
 * A sticky note (Huanrui, 2026-10-06).
 *
 * Words on the board and nowhere else: not a picture, not a skill, never sent
 * to a model, to Miro or to the library. Put down from the skill rail or the
 * wheel's quick skills like any card, and it arrives ready to type in.
 *
 * Two states, so that it can be both moved and written on. At rest it is a
 * piece of paper: a press anywhere drags it, as with everything else on the
 * board. A double-click (or arriving new) turns it into a text box, where a
 * press places the cursor instead; clicking away, or Escape, puts it back.
 * Everything typed in one go is one step on the undo stack.
 *
 * Paper rather than yellow: the interface is black and white, and colour on
 * the board belongs to the pictures.
 */

export interface NoteNodeData extends Record<string, unknown> {
  text?: string
  /** Handed in by the board, never stored: the text as it is typed. */
  onText?: (text: string) => void
  /** Handed in by the board, never stored: called once as writing begins. */
  onWriteStart?: () => void
}

export const NoteNode = memo(function NoteNode({ data, selected }: NodeProps) {
  const note = data as NoteNodeData
  // A new note opens for writing; one loaded with the board does not.
  const [writing, setWriting] = useState(() => !note.text)
  const box = useRef<HTMLTextAreaElement | null>(null)

  // As tall as its text, so the paper can centre it; it scrolls once it
  // reaches the paper's edge (max-height in canvas.css).
  useEffect(() => {
    const area = box.current
    if (!writing || !area) return
    area.style.height = 'auto'
    area.style.height = `${area.scrollHeight}px`
  })

  useEffect(() => {
    if (!writing) return
    note.onWriteStart?.()
    const area = box.current
    if (!area) return
    area.focus()
    area.setSelectionRange(area.value.length, area.value.length)
    // Once each time it is opened, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writing])

  return (
    <div className={`cnote${selected ? ' cnote--selected' : ''}${writing ? ' cnote--writing' : ''}`}>
      <NodeResizer
        isVisible={selected}
        minWidth={120}
        minHeight={90}
        lineClassName="cnode__resize-line"
        handleClassName="cnode__resize-handle"
      />
      {writing ? (
        <textarea
          ref={box}
          className="cnote__text cnote__area nodrag nowheel nopan"
          rows={1}
          value={note.text ?? ''}
          placeholder="Write something…"
          spellCheck
          onChange={(event) => note.onText?.(event.target.value)}
          onBlur={() => setWriting(false)}
          onKeyDown={(event) => {
            // The board's own keys (Delete, Ctrl+Z, arrows) stay out of it.
            event.stopPropagation()
            if (event.key === 'Escape') event.currentTarget.blur()
          }}
        />
      ) : (
        <div
          className={`cnote__text${note.text ? '' : ' cnote__text--empty'}`}
          onDoubleClick={(event) => {
            event.stopPropagation()
            setWriting(true)
          }}
        >
          {note.text || 'Double-click to write'}
        </div>
      )}
    </div>
  )
})
